/**
 * Table Component
 *
 * Accessible data table with sorting, selection, and responsive design.
 * Follows WCAG 2.1 Level AA guidelines.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface TableProps extends ComponentPropsWithoutRef<'table'> {
  /**
   * Makes the table scrollable on small screens
   * @default true
   */
  responsive?: boolean;
}

export type TableHeaderProps = ComponentPropsWithoutRef<'thead'>;

export type TableBodyProps = ComponentPropsWithoutRef<'tbody'>;

export type TableFooterProps = ComponentPropsWithoutRef<'tfoot'>;

export interface TableRowProps extends ComponentPropsWithoutRef<'tr'> {
  /**
   * Highlight row on hover
   * @default true
   */
  hoverable?: boolean;
  /**
   * Make row selectable (clickable)
   */
  selectable?: boolean;
  /**
   * Mark row as selected
   */
  selected?: boolean;
}

export interface TableHeadProps extends ComponentPropsWithoutRef<'th'> {
  /**
   * Enable sorting for this column
   */
  sortable?: boolean;
  /**
   * Current sort direction
   */
  sortDirection?: 'asc' | 'desc' | null;
  /**
   * Callback when sort is triggered
   */
  onSort?: () => void;
}

export type TableCellProps = ComponentPropsWithoutRef<'td'>;

export type TableCaptionProps = ComponentPropsWithoutRef<'caption'>;

/**
 * Table Root
 */
const Table = forwardRef<ElementRef<'table'>, TableProps>(
  ({ className, responsive = true, ...props }, ref) => {
    const table = (
      <table
        ref={ref}
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    );

    if (responsive) {
      return (
        <div className="relative w-full overflow-auto">
          {table}
        </div>
      );
    }

    return table;
  }
);
Table.displayName = 'Table';

/**
 * Table Header
 */
const TableHeader = forwardRef<ElementRef<'thead'>, TableHeaderProps>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        'border-b border-neutral-200 dark:border-neutral-800',
        '[&_tr]:border-b',
        className
      )}
      {...props}
    />
  )
);
TableHeader.displayName = 'TableHeader';

/**
 * Table Body
 */
const TableBody = forwardRef<ElementRef<'tbody'>, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
);
TableBody.displayName = 'TableBody';

/**
 * Table Footer
 */
const TableFooter = forwardRef<ElementRef<'tfoot'>, TableFooterProps>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn(
        'border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 font-medium',
        '[&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
);
TableFooter.displayName = 'TableFooter';

/**
 * Table Row
 */
const TableRow = forwardRef<ElementRef<'tr'>, TableRowProps>(
  ({ className, hoverable = true, selectable, selected, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-neutral-200 dark:border-neutral-800 transition-colors',
        hoverable && 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
        selectable && 'cursor-pointer',
        selected && 'bg-primary-50 dark:bg-primary-900/30',
        'h-9',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

/**
 * Table Head Cell
 */
const TableHead = forwardRef<ElementRef<'th'>, TableHeadProps>(
  ({ className, sortable, sortDirection, onSort, children, ...props }, ref) => {
    const content = (
      <>
        {children}
        {sortable && (
          <span className="ml-2 inline-flex">
            {sortDirection === 'asc' && <ChevronUp className="h-4 w-4" />}
            {sortDirection === 'desc' && <ChevronDown className="h-4 w-4" />}
            {sortDirection === null && <ChevronsUpDown className="h-4 w-4 opacity-50" />}
          </span>
        )}
      </>
    );

    return (
      <th
        ref={ref}
        className={cn(
          'h-11 px-4 text-left align-middle font-medium text-neutral-700 dark:text-neutral-300',
          sortable && 'cursor-pointer select-none hover:text-neutral-900 dark:hover:text-neutral-100',
          // Focus ring for keyboard navigation - WCAG 2.1 AA
          sortable &&
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900',
          className
        )}
        onClick={sortable ? onSort : undefined}
        onKeyDown={
          sortable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSort?.();
                }
              }
            : undefined
        }
        tabIndex={sortable ? 0 : undefined}
        role={sortable ? 'button' : undefined}
        aria-sort={
          sortable
            ? sortDirection === 'asc'
              ? 'ascending'
              : sortDirection === 'desc'
              ? 'descending'
              : 'none'
            : undefined
        }
        {...props}
      >
        {content}
      </th>
    );
  }
);
TableHead.displayName = 'TableHead';

/**
 * Table Cell
 */
const TableCell = forwardRef<ElementRef<'td'>, TableCellProps>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('p-4 align-middle text-neutral-900 dark:text-neutral-100', className)}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

/**
 * Table Caption - for accessibility
 */
const TableCaption = forwardRef<ElementRef<'caption'>, TableCaptionProps>(
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn('mt-4 text-sm text-neutral-600 dark:text-neutral-400', className)}
      {...props}
    />
  )
);
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
};
